import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Action, Selector, State, StateContext, Store } from '@ngxs/store';
import { SessionState } from '@platform-frontend/account';
import {
  BillingAddress,
  BillingAddressCreated,
  BillingAddressDeleted,
  BillingAddressUpdated,
  CountryState,
  MARKETPLACE_ERROR_HANDLERS,
  MarketplaceBillingInformationState
} from '@platform-frontend/marketplace';
import { PLATFORM_ENVIRONMENT } from '@platform-frontend/platform';
import { InvalidStateError, RefreshWindow, ShowErrorNotification, convertToHttpParams, handleErrors, throwIfNullish } from '@platform-frontend/util';
import { Observable, combineLatest, of } from 'rxjs';
import { filter, map, switchMap, take, tap } from 'rxjs/operators';
import { FetchFlowData, FlowCanceled, FlowPreparationStarted, PublishFetchedFlowData, SetFlow } from '../../flow/flow.actions';
import { MSG_SELECT_BILLING_ADDRESS_DEFAULT } from '../../notification-messages';
import { PricingRelevantBillingInfos } from './model/pricing-relevant-infos';
import { ShoppingCartBillingStateModel } from './model/shopping-cart-billing-state-model';
import { SelectBillingAddress, SelectCountry } from './shopping-cart-billing.actions';

const DEFAULT_STATE: ShoppingCartBillingStateModel = {
  selectedCountry: 'DE',
  loadedSelectedCountry: 'DE',
  selectedBillingAddress: null,
  loadedSelectedBillingAddress: null
};

@State<ShoppingCartBillingStateModel>({
  name: 'shoppingCartBilling',
  defaults: DEFAULT_STATE
})
@Injectable()
export class ShoppingCartBillingState {
  constructor(private http: HttpClient, private store: Store) {}

  isSignedIn$: Observable<boolean> = this.store.select(SessionState.isSignedIn);
  countries$: Observable<string[]> = this.store.select(CountryState.countries);
  billingAddresses$: Observable<BillingAddress[]> = this.store.select(MarketplaceBillingInformationState.billingAddresses);

  @Selector()
  static selectedCountry(state: ShoppingCartBillingStateModel): string {
    return state.selectedCountry;
  }

  @Selector()
  static selectedBillingAddress(state: ShoppingCartBillingStateModel): BillingAddress | null {
    return state.selectedBillingAddress;
  }

  @Selector()
  static pricingRelevantBillingInfos(state: ShoppingCartBillingStateModel): PricingRelevantBillingInfos {
    return {
      countryCode: state.selectedCountry,
      billingInformationId: state.selectedBillingAddress?.id ?? null
    };
  }

  @Action(FlowPreparationStarted)
  initializeCountry(ctx: StateContext<ShoppingCartBillingStateModel>, { flow }: FlowPreparationStarted): Observable<unknown> {
    if (flow.type !== 'COMMERCIAL_CHECKOUT') {
      return of(null);
    }

    return this.isSignedIn$.pipe(
      take(1),
      filter(isSignedIn => !isSignedIn),
      switchMap(() => this.fetchSelectedCountry()),
      tap(selectedCountry => ctx.patchState({ selectedCountry }))
    );
  }

  @Action(FetchFlowData)
  loadCountryAndBillingAddress(ctx: StateContext<ShoppingCartBillingStateModel>, { flow }: FetchFlowData): Observable<unknown> {
    if (flow.type !== 'COMMERCIAL_CHECKOUT') {
      return of(null);
    }

    return combineLatest([
      this.fetchSelectedCountry(),
      this.http.get<BillingAddress | null>(PLATFORM_ENVIRONMENT.apiUrl + '/billingInformation/active', {
        params: convertToHttpParams({ contractId: flow.startParams.contractId })
      })
    ]).pipe(tap(([loadedSelectedCountry, loadedSelectedBillingAddress]) => ctx.patchState({ loadedSelectedCountry, loadedSelectedBillingAddress })));
  }

  private fetchSelectedCountry(): Observable<string> {
    return this.http.get<string>(PLATFORM_ENVIRONMENT.apiUrl + '/billingInformation/activeCountry');
  }

  @Action(PublishFetchedFlowData)
  publishCountryAndBillingAddress(ctx: StateContext<ShoppingCartBillingStateModel>): void {
    const { loadedSelectedCountry, loadedSelectedBillingAddress } = ctx.getState();
    ctx.patchState({
      selectedCountry: loadedSelectedCountry,
      loadedSelectedCountry: DEFAULT_STATE.loadedSelectedCountry,
      selectedBillingAddress: loadedSelectedBillingAddress,
      loadedSelectedBillingAddress: DEFAULT_STATE.loadedSelectedBillingAddress
    });
  }

  @Action(SetFlow)
  @Action(RefreshWindow)
  @Action(FlowCanceled)
  reset(ctx: StateContext<ShoppingCartBillingStateModel>): void {
    const { selectedCountry } = ctx.getState();
    ctx.patchState({ ...DEFAULT_STATE, selectedCountry: selectedCountry });
  }

  @Action(SelectCountry)
  selectCountry(ctx: StateContext<ShoppingCartBillingStateModel>, { country }: SelectCountry): Observable<unknown> {
    return this.getCountryByCode(country).pipe(
      tap(selectedCountry => ctx.patchState({ selectedCountry, selectedBillingAddress: null })),
      handleErrors(MARKETPLACE_ERROR_HANDLERS)
    );
  }

  @Action(SelectBillingAddress)
  selectBillingAddress(ctx: StateContext<ShoppingCartBillingStateModel>, { billingAddressId }: SelectBillingAddress): Observable<unknown> {
    return this.getBillingAddressById(billingAddressId).pipe(
      handleErrors(MARKETPLACE_ERROR_HANDLERS, {
        DEFAULT: new ShowErrorNotification(MSG_SELECT_BILLING_ADDRESS_DEFAULT)
      }),
      switchMap(billingAddress => this.patchState(ctx, billingAddress))
    );
  }

  @Action(BillingAddressCreated)
  autoSelectCreatedBillingAddress(ctx: StateContext<ShoppingCartBillingStateModel>, { billingAddress }: BillingAddressCreated): Observable<unknown> {
    return this.patchState(ctx, billingAddress);
  }

  @Action(BillingAddressUpdated)
  updateSelectedBillingAddress(ctx: StateContext<ShoppingCartBillingStateModel>, { original, update }: BillingAddressUpdated): Observable<unknown> {
    if (original.id === ctx.getState().selectedBillingAddress?.id) {
      return this.patchState(ctx, update);
    } else {
      return of(null);
    }
  }

  @Action(BillingAddressDeleted)
  deleteSelectedBillingAddress(ctx: StateContext<ShoppingCartBillingStateModel>, { billingAddress }: BillingAddressDeleted): Observable<unknown> {
    if (billingAddress.id === ctx.getState().selectedBillingAddress?.id) {
      return this.billingAddresses$.pipe(
        take(1),
        map(billingAddresses => billingAddresses[0] ?? null),
        switchMap(newBillingAddress => this.patchState(ctx, newBillingAddress))
      );
    } else {
      return of(null);
    }
  }

  private patchState(ctx: StateContext<ShoppingCartBillingStateModel>, selectedBillingAddress: BillingAddress | null): Observable<unknown> {
    if (selectedBillingAddress === null) {
      ctx.patchState({ selectedBillingAddress });
      return of(null);
    }

    return this.getCountryByCode(selectedBillingAddress.country).pipe(
      tap(selectedCountry => ctx.patchState({ selectedCountry, selectedBillingAddress })),
      handleErrors(MARKETPLACE_ERROR_HANDLERS)
    );
  }

  private getCountryByCode(countryCode: string): Observable<string> {
    return this.countries$.pipe(
      take(1),
      map(countries => countries.find(country => country === countryCode)),
      throwIfNullish(() => new InvalidStateError(`Country code ${countryCode} does not correspond to a known country.`))
    );
  }

  private getBillingAddressById(billingAddressId: number | null): Observable<BillingAddress | null> {
    if (billingAddressId === null) {
      return of(null);
    }
    return this.billingAddresses$.pipe(
      take(1),
      map(billingAddresses => billingAddresses.find(billingAddress => billingAddress.id === billingAddressId)),
      throwIfNullish(() => new InvalidStateError(`Id ${billingAddressId} does not correspond to a known billing address.`))
    );
  }
}
